/**
 * Host OS — Knowledge Base. The host's source of truth: connect a website
 * (full crawl), add extra pages, upload files, write notes and FAQs. Everything
 * is embedded for semantic retrieval and powers the "Ask" box + future AI tools.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  BookOpen, Globe, Link2, FileUp, StickyNote, HelpCircle, Loader2, Trash2,
  RefreshCw, Sparkles, Send, CheckCircle2, AlertCircle, Clock, Database, FileText,
  Eye, MapPin, ShieldCheck, Plus, Building2, X, AlertTriangle,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";
import HostLayout from "@/components/host/HostLayout";
import { HostPageHeader, KpiCard } from "@/components/host/hostUi";

const TYPE_META = {
  website: { icon: Globe, label: "Website", cls: "bg-blue-500/15 text-blue-600 dark:text-blue-300" },
  url: { icon: Link2, label: "Web page", cls: "bg-sky-500/15 text-sky-600 dark:text-sky-300" },
  file: { icon: FileText, label: "File", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-300" },
  manual: { icon: StickyNote, label: "Note", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" },
  faq: { icon: HelpCircle, label: "FAQ", cls: "bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-300" },
  web: { icon: Globe, label: "Public web", cls: "bg-orange-500/15 text-orange-600 dark:text-orange-300" },
};

const STATUS_META = {
  ready: { icon: CheckCircle2, label: "Ready", cls: "text-emerald-600" },
  importing: { icon: Clock, label: "Importing…", cls: "text-amber-600" },
  processing: { icon: Clock, label: "Importing…", cls: "text-amber-600" },
  needs_attention: { icon: AlertTriangle, label: "Needs attention", cls: "text-orange-600" },
  error: { icon: AlertCircle, label: "Failed", cls: "text-red-600" },
  failed: { icon: AlertCircle, label: "Failed", cls: "text-red-600" },
};

const PROCESSING = new Set(["processing", "importing"]);

const HostKnowledgeBasePage = () => {
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  // composer state
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [pageUrl, setPageUrl] = useState("");
  const [noteTitle, setNoteTitle] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [faqQ, setFaqQ] = useState("");
  const [faqA, setFaqA] = useState("");
  const fileRef = useRef(null);

  // ask box
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState(null);

  // profile + corrections
  const [profile, setProfile] = useState(null);
  const [corrections, setCorrections] = useState([]);
  const [corrField, setCorrField] = useState("");
  const [corrValue, setCorrValue] = useState("");
  const [corrScope, setCorrScope] = useState("");
  // inline saved-content viewer
  const [openContent, setOpenContent] = useState(null);
  const [contentCache, setContentCache] = useState({});
  const [webFallback, setWebFallback] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const [sRes, stRes, pRes] = await Promise.all([
        fetch(`${API}/host/kb/sources`, { headers }),
        fetch(`${API}/host/kb/stats`, { headers }),
        fetch(`${API}/host/kb/profile`, { headers }),
      ]);
      if (sRes.ok) setSources(await sRes.json());
      if (stRes.ok) setStats(await stRes.json());
      if (pRes.ok) { const p = await pRes.json(); setProfile(p.profile); setCorrections(p.corrections || []); }
      try {
        const setRes = await fetch(`${API}/host/kb/settings`, { headers });
        if (setRes.ok) setWebFallback((await setRes.json()).web_fallback);
      } catch (e) { /* ignore */ }
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Poll while anything is processing
  useEffect(() => {
    const anyProcessing = sources.some((s) => PROCESSING.has(s.status));
    if (anyProcessing && !pollRef.current) {
      pollRef.current = setInterval(fetchAll, 4000);
    } else if (!anyProcessing && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [sources, fetchAll]);

  const post = async (path, body) => {
    const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
    const res = await fetch(`${API}/host/kb/${path}`, { method: "POST", headers, body: JSON.stringify(body) });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || "Something went wrong");
    }
    return res.json();
  };

  const addWebsite = async () => {
    if (!websiteUrl.trim()) return;
    setBusy(true);
    try {
      await post("website", { url: websiteUrl.trim() });
      setWebsiteUrl("");
      toast.success("Crawling your website — this runs in the background");
      fetchAll();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const addUrl = async () => {
    if (!pageUrl.trim()) return;
    setBusy(true);
    try {
      await post("url", { url: pageUrl.trim() });
      setPageUrl("");
      toast.success("Reading that page…");
      fetchAll();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!noteTitle.trim() || !noteContent.trim()) { toast.error("Add a title and content"); return; }
    setBusy(true);
    try {
      await post("manual", { title: noteTitle.trim(), content: noteContent.trim() });
      setNoteTitle(""); setNoteContent("");
      toast.success("Note added to your knowledge base");
      fetchAll();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const addFaq = async () => {
    if (!faqQ.trim() || !faqA.trim()) { toast.error("Add a question and answer"); return; }
    setBusy(true);
    try {
      await post("faq", { question: faqQ.trim(), answer: faqA.trim() });
      setFaqQ(""); setFaqA("");
      toast.success("FAQ added");
      fetchAll();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const addFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/kb/file`, { method: "POST", headers, body: form });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Upload failed"); }
      toast.success("File added to your knowledge base");
      fetchAll();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this from your knowledge base?")) return;
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/kb/sources/${id}`, { method: "DELETE", headers });
      if (res.ok) { setSources((p) => p.filter((s) => s.source_id !== id)); toast.success("Removed"); fetchAll(); }
    } catch (e) { toast.error("Couldn't remove"); }
  };

  const refresh = async (id) => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/kb/reprocess/${id}`, { method: "POST", headers });
      if (res.ok) { toast.success("Re-crawling…"); fetchAll(); }
    } catch (e) { toast.error("Couldn't refresh"); }
  };

  const ask = async () => {
    if (!question.trim()) return;
    setAsking(true); setAnswer(null);
    try {
      const data = await post("query", { query: question.trim(), top_k: 6 });
      setAnswer(data);
    } catch (e) { toast.error(e.message); } finally { setAsking(false); }
  };

  const hasData = (stats?.total_sources || 0) > 0;

  const toggleContent = async (id) => {
    if (openContent === id) { setOpenContent(null); return; }
    setOpenContent(id);
    if (!contentCache[id]) {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${API}/host/kb/sources/${id}/content`, { headers });
        if (res.ok) { const d = await res.json(); setContentCache((c) => ({ ...c, [id]: d.pages || [] })); }
      } catch (e) { /* ignore */ }
    }
  };

  const addCorrection = async () => {
    if (!corrField.trim() || !corrValue.trim()) { toast.error("Add a field and the correct value"); return; }
    try {
      await post("corrections", { field: corrField.trim(), value: corrValue.trim(), scope_location: corrScope.trim() || null });
      setCorrField(""); setCorrValue(""); setCorrScope("");
      toast.success("Correction saved — it now overrides imported content");
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  const removeCorrection = async (id) => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/kb/corrections/${id}`, { method: "DELETE", headers });
      if (res.ok) { toast.success("Correction removed"); fetchAll(); }
    } catch (e) { toast.error("Couldn't remove"); }
  };

  const toggleWebFallback = async (val) => {
    setWebFallback(val);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      await fetch(`${API}/host/kb/settings`, { method: "PUT", headers, body: JSON.stringify({ web_fallback: val }) });
      toast.success(val ? "Web fallback on — labeled clearly, never overrides your sources" : "Web fallback off — answers only from your sources");
    } catch (e) { toast.error("Couldn't save setting"); }
  };

  return (
    <HostLayout>
      <div data-testid="host-kb-page">
        <HostPageHeader
          icon={BookOpen}
          title="Knowledge Base"
          subtitle="Your single source of truth. Connect your website, files and notes — the platform and every AI tool we build will draw its answers from here."
        />

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <KpiCard label="Sources" value={stats ? stats.total_sources : "—"} icon={Database} />
          <KpiCard label="Ready" value={stats ? stats.ready : "—"} icon={CheckCircle2} accent="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" />
          <KpiCard label="Documents" value={stats ? stats.total_chunks : "—"} icon={Sparkles} accent="bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300" />
          <KpiCard label="Characters indexed" value={stats ? stats.total_chars.toLocaleString() : "—"} icon={FileText} accent="bg-blue-500/15 text-blue-700 dark:text-blue-300" />
        </div>

        {/* Composer */}
        <Card className="p-5 mb-6" data-testid="kb-composer">
          <h2 className="font-display text-xl mb-4">Add to your knowledge base</h2>
          <Tabs defaultValue="website">
            <TabsList className="flex flex-wrap h-auto gap-1" data-testid="kb-composer-tabs">
              <TabsTrigger value="website" className="gap-1.5" data-testid="kb-tab-website"><Globe className="w-4 h-4" /> Website</TabsTrigger>
              <TabsTrigger value="url" className="gap-1.5" data-testid="kb-tab-url"><Link2 className="w-4 h-4" /> Web page</TabsTrigger>
              <TabsTrigger value="file" className="gap-1.5" data-testid="kb-tab-file"><FileUp className="w-4 h-4" /> File</TabsTrigger>
              <TabsTrigger value="note" className="gap-1.5" data-testid="kb-tab-note"><StickyNote className="w-4 h-4" /> Note</TabsTrigger>
              <TabsTrigger value="faq" className="gap-1.5" data-testid="kb-tab-faq"><HelpCircle className="w-4 h-4" /> FAQ</TabsTrigger>
            </TabsList>

            <TabsContent value="website" className="mt-4 space-y-3">
              <p className="text-sm text-muted-foreground">Paste your website — we&apos;ll crawl every page on the domain and learn it all.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="yourclub.com" onKeyDown={(e) => e.key === "Enter" && addWebsite()} data-testid="kb-website-input" />
                <Button onClick={addWebsite} disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5 shrink-0" data-testid="kb-website-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />} Connect website
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="url" className="mt-4 space-y-3">
              <p className="text-sm text-muted-foreground">Add a specific page (a blog post, a booking policy, an external listing).</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input value={pageUrl} onChange={(e) => setPageUrl(e.target.value)} placeholder="https://example.com/rules" onKeyDown={(e) => e.key === "Enter" && addUrl()} data-testid="kb-url-input" />
                <Button onClick={addUrl} disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5 shrink-0" data-testid="kb-url-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Add page
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="file" className="mt-4 space-y-3">
              <p className="text-sm text-muted-foreground">Upload rules, waivers, brochures or price lists. PDF, DOCX, TXT or MD (max 15MB).</p>
              <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden" onChange={(e) => addFile(e.target.files?.[0])} data-testid="kb-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={busy} variant="outline" className="gap-1.5" data-testid="kb-file-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />} Choose a file
              </Button>
            </TabsContent>

            <TabsContent value="note" className="mt-4 space-y-3">
              <div>
                <Label className="text-sm">Title</Label>
                <Input value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)} placeholder="e.g. Cancellation policy" className="mt-1.5" data-testid="kb-note-title" />
              </div>
              <div>
                <Label className="text-sm">Content</Label>
                <Textarea value={noteContent} onChange={(e) => setNoteContent(e.target.value)} rows={5} placeholder="Write anything the platform should know…" className="mt-1.5" data-testid="kb-note-content" />
              </div>
              <Button onClick={addNote} disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5" data-testid="kb-note-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <StickyNote className="w-4 h-4" />} Save note
              </Button>
            </TabsContent>

            <TabsContent value="faq" className="mt-4 space-y-3">
              <div>
                <Label className="text-sm">Question</Label>
                <Input value={faqQ} onChange={(e) => setFaqQ(e.target.value)} placeholder="e.g. Where do I park?" className="mt-1.5" data-testid="kb-faq-question" />
              </div>
              <div>
                <Label className="text-sm">Answer</Label>
                <Textarea value={faqA} onChange={(e) => setFaqA(e.target.value)} rows={4} placeholder="Give the answer players should get…" className="mt-1.5" data-testid="kb-faq-answer" />
              </div>
              <Button onClick={addFaq} disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5" data-testid="kb-faq-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <HelpCircle className="w-4 h-4" />} Add FAQ
              </Button>
            </TabsContent>
          </Tabs>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Sources list */}
          <div>
            <h2 className="font-display text-xl mb-3">Your sources</h2>
            {loading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : sources.length === 0 ? (
              <Card className="p-8 text-center border-dashed" data-testid="kb-sources-empty">
                <BookOpen className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="font-medium">Nothing here yet</p>
                <p className="text-xs text-muted-foreground mt-1">Connect your website above to teach the platform everything about your events.</p>
              </Card>
            ) : (
              <div className="space-y-2" data-testid="kb-sources-list">
                {sources.map((s) => {
                  const tm = TYPE_META[s.type] || TYPE_META.manual;
                  const sm = STATUS_META[s.status] || STATUS_META.processing;
                  const TypeIcon = tm.icon; const StatusIcon = sm.icon;
                  return (
                    <Card key={s.source_id} className="p-3.5 flex items-start gap-3" data-testid={`kb-source-${s.source_id}`}>
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${tm.cls}`}>
                        <TypeIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{s.title || s.url || s.original_filename || tm.label}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className={`inline-flex items-center gap-1 text-[11px] ${sm.cls}`}>
                            <StatusIcon className={`w-3 h-3 ${PROCESSING.has(s.status) ? "animate-pulse" : ""}`} /> {sm.label}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {s.type === "website"
                              ? (s.pages_attempted > 0 && `${s.pages_saved}/${s.pages_attempted} pages saved${s.pages_failed ? ` · ${s.pages_failed} failed` : ""}`)
                              : (s.pages_saved > 0 && `${s.char_count?.toLocaleString?.() || s.char_count} chars`)}
                          </span>
                        </div>
                        {(s.status === "error" || s.status === "failed" || s.status === "needs_attention") && s.error && (
                          <p className={`text-[11px] mt-0.5 ${s.status === "needs_attention" ? "text-orange-600" : "text-red-600"}`}>{s.error}</p>
                        )}
                        {PROCESSING.has(s.status) && (
                          <div className="mt-2" data-testid={`kb-progress-${s.source_id}`}>
                            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                              {(() => {
                                const pct = s.type === "website"
                                  ? Math.min(92, Math.max(6, ((s.crawl_total > 0 ? s.crawled_count / s.crawl_total : s.crawled_count / 60) * 100) || 6))
                                  : null;
                                return pct === null ? (
                                  <div className="h-full w-1/3 rounded-full bg-primary kb-indeterminate" />
                                ) : (
                                  <div className="h-full rounded-full bg-primary transition-all duration-700 ease-out" style={{ width: `${pct}%` }} />
                                );
                              })()}
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1">
                              {s.type === "website"
                                ? (s.crawled_count > 0
                                    ? `Crawled ${s.crawled_count} page${s.crawled_count === 1 ? "" : "s"} so far — usually 30–90s`
                                    : "Starting crawl — usually 30–90s")
                                : "Indexing…"}
                            </p>
                          </div>
                        )}
                        {openContent === s.source_id && (
                          <div className="mt-2 rounded-lg border border-border bg-muted/30 p-2 max-h-64 overflow-auto" data-testid={`kb-content-${s.source_id}`}>
                            {!contentCache[s.source_id] ? (
                              <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading saved content…</p>
                            ) : contentCache[s.source_id].length === 0 ? (
                              <p className="text-[11px] text-muted-foreground">No saved content.</p>
                            ) : (
                              contentCache[s.source_id].map((pg, pi) => (
                                <div key={pi} className="mb-2 last:mb-0">
                                  {(pg.title || pg.page_url) && <p className="text-[11px] font-medium truncate">{pg.title || pg.page_url}</p>}
                                  <p className="text-[11px] text-muted-foreground whitespace-pre-wrap leading-relaxed">{(pg.text || "").slice(0, 1500)}{(pg.text || "").length > 1500 ? "…" : ""}</p>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        {s.pages_saved > 0 && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => toggleContent(s.source_id)} aria-label="View saved content" data-testid={`kb-view-${s.source_id}`}>
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {(s.type === "website" || s.type === "url") && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => refresh(s.source_id)} aria-label="Refresh" data-testid={`kb-refresh-${s.source_id}`}>
                            <RefreshCw className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(s.source_id)} aria-label="Remove" data-testid={`kb-delete-${s.source_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Ask box */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-primary-ink" />
              <h2 className="font-display text-xl">Ask your knowledge base</h2>
            </div>
            <Card className="p-5" data-testid="kb-ask">
              <p className="text-sm text-muted-foreground mb-3">Test what the AI knows. This is the same retrieval every Host Hub tool will use.</p>
              <div className="flex items-center justify-between gap-3 mb-3 p-2.5 rounded-lg bg-muted/40 border border-border">
                <div className="min-w-0">
                  <p className="text-xs font-medium flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-orange-600" /> Fill gaps from the public web</p>
                  <p className="text-[11px] text-muted-foreground">When your sources don&apos;t cover it, search the web. Answers are clearly labeled and never override your sources.</p>
                </div>
                <Switch checked={webFallback} onCheckedChange={toggleWebFallback} data-testid="kb-web-fallback-toggle" />
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="e.g. What's your refund policy?" onKeyDown={(e) => e.key === "Enter" && ask()} disabled={!hasData || asking} data-testid="kb-ask-input" />
                <Button onClick={ask} disabled={!hasData || asking} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5 shrink-0" data-testid="kb-ask-submit">
                  {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Ask
                </Button>
              </div>
              {!hasData && <p className="text-xs text-muted-foreground">Add at least one source to start asking.</p>}
              {asking && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Searching your knowledge base…</div>}
              {answer && !asking && (
                <div data-testid="kb-answer">
                  {answer.from_web && (
                    <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full bg-orange-500/15 text-orange-700 dark:text-orange-300" data-testid="kb-answer-web-badge">
                      <Globe className="w-3 h-3" /> From the public web — not your saved sources, please verify
                    </div>
                  )}
                  <div className="p-4 rounded-lg bg-muted/40 border border-border">
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{answer.answer}</p>
                  </div>
                  {answer.sources?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Sources</p>
                      <div className="space-y-1.5">
                        {answer.sources.map((src, i) => {
                          const tm = TYPE_META[src.source_type] || TYPE_META.manual;
                          const Icon = tm.icon;
                          return (
                            <div key={i} className="flex items-start gap-2 text-xs" data-testid={`kb-answer-source-${i}`}>
                              <span className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${tm.cls}`}><Icon className="w-3 h-3" /></span>
                              <span className="text-muted-foreground truncate">
                                <span className="font-medium text-foreground">[{i + 1}] {src.source_label}</span>
                                {src.page_url && <span> · {src.page_url}</span>}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Business profile + corrections */}
        <Card className="p-5 mt-6" data-testid="kb-profile-card">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-5 h-5 text-primary-ink" />
            <h2 className="font-display text-xl">Business profile</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Auto-extracted from your sources to keep key facts (like every location) reliable.
            Shown as <span className="font-medium">imported</span> — add a correction to override anything that&apos;s wrong.
          </p>

          <div className="grid md:grid-cols-2 gap-5">
            {/* Locations */}
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
                Locations {profile?.locations?.length ? `(${profile.locations.length})` : ""}
              </p>
              {profile?.locations?.length ? (
                <div className="space-y-2" data-testid="kb-profile-locations">
                  {profile.locations.map((l, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 text-primary-ink shrink-0" />
                      <span>
                        <span className="font-medium">{[l.brand, l.name].filter(Boolean).join(" ") || "Location"}</span>
                        {(l.street_address || l.city) && (
                          <span className="text-muted-foreground"> — {[l.street_address, [l.postal_code, l.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No locations detected yet. Import your website or a locations/FAQ page.</p>
              )}
            </div>

            {/* Corrections */}
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" /> Owner corrections
              </p>
              <div className="space-y-2 mb-3" data-testid="kb-corrections-list">
                {corrections.length === 0 && <p className="text-xs text-muted-foreground">No corrections yet. These override imported content.</p>}
                {corrections.map((c) => (
                  <div key={c.correction_id} className="flex items-start gap-2 text-sm bg-muted/40 rounded-lg p-2" data-testid={`kb-correction-${c.correction_id}`}>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">{c.field}</span>
                      {c.scope_location && <span className="text-[11px] ml-1 px-1.5 py-0.5 rounded bg-primary/15 text-primary-ink">{c.scope_location}</span>}
                      <span className="text-muted-foreground"> — {c.value}</span>
                    </div>
                    <button onClick={() => removeCorrection(c.correction_id)} className="text-muted-foreground hover:text-destructive shrink-0" aria-label="Delete correction" data-testid={`kb-correction-del-${c.correction_id}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <Input value={corrField} onChange={(e) => setCorrField(e.target.value)} placeholder="Field (e.g. Cancellation policy)" data-testid="kb-corr-field" />
                <Input value={corrValue} onChange={(e) => setCorrValue(e.target.value)} placeholder="Correct value (e.g. 24 hours before class)" data-testid="kb-corr-value" />
                <Input value={corrScope} onChange={(e) => setCorrScope(e.target.value)} placeholder="Applies to (optional, e.g. BEYOND Mitte)" data-testid="kb-corr-scope" />
                <Button onClick={addCorrection} variant="outline" className="gap-1.5 w-full" data-testid="kb-corr-submit"><Plus className="w-4 h-4" /> Save correction</Button>
              </div>
            </div>
          </div>

          {stats && stats.openai_file_search === false && (
            <p className="text-[11px] text-muted-foreground mt-4 pt-3 border-t border-border">
              Answering runs on the built-in engine (Emergent OpenAI · {stats ? "" : ""}gpt-4o-mini). To switch to OpenAI managed File Search, add an <span className="font-mono">OPENAI_API_KEY</span> to the backend.
            </p>
          )}
        </Card>
      </div>
    </HostLayout>
  );
};

export default HostKnowledgeBasePage;
